import classnames from "classnames";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { format } from "date-fns";
import { Maximize2, Minimize2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Html } from "@/components/html/html";
import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useLocale, useTranslation } from "@/lib/i18n";
import { useNow } from "@/features/shared/hooks";
import useLayoutStore from "@/store/useLayoutStore";

import { buildRiskPicture } from "./build-risk-picture";
import { clampDayIndex } from "./cell-label";
import { riskPictureDayTitle } from "./risk-picture-day-title";
import type { RiskPictureCopy } from "./risk-picture-model";
import { RiskPictureMap } from "./risk-picture-map";
import {
  clearRiskPictureWarning,
  riskPictureMapPreview,
  type RiskPictureSelection,
  selectRiskPictureDay,
  selectRiskPictureWarning,
} from "./risk-picture-selection";
import { RiskPictureTable } from "./risk-picture-table";
import {
  fetchRiskPictureLegacyComment,
  riskPictureLegacyCommentKey,
} from "./risk-picture-legacy-comment";
import { useRiskPictureWarningSetsQuery } from "./use-risk-picture-warning-sets";
import { riskPictureNow } from "./risk-picture-now";
import { buildRiskPictureDemo, riskPictureDemoEnabled } from "./risk-picture-demo";
import { riskPictureUpdated } from "./risk-picture-updated";
import styles from "./risk-picture.module.css";

interface RiskPictureProps {
  copy: RiskPictureCopy;
}

/**
 * Actuele waarschuwingen & risicobeeld: an 8-day phenomenon table with per-day
 * thumbnails, and a full map with a day sidebar. All of them share one `selection`:
 * a day, plus optionally the warning opened in the sidebar.
 */
export const RiskPicture = ({ copy }: RiskPictureProps) => {
  const [demoPayload] = useState(() =>
    riskPictureDemoEnabled() ? buildRiskPictureDemo(riskPictureNow()) : undefined,
  );
  const warningSetsQuery = useRiskPictureWarningSetsQuery({ enabled: !demoPayload });
  const legacyCommentQuery = useQuery({
    queryKey: riskPictureLegacyCommentKey(),
    queryFn: fetchRiskPictureLegacyComment,
  });
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const { t } = useTranslation();
  const locale = useLocale();
  const title = copy.title;
  const description = copy.description;

  const now = riskPictureNow(useNow(60 * 1000));
  const payload =
    demoPayload ??
    (warningSetsQuery.data === null ? { warnings: [] } : warningSetsQuery.data);
  const model = useMemo(
    () => (payload ? buildRiskPicture(payload, { now }) : undefined),
    [payload, now],
  );
  const updated = useMemo(
    () => (payload ? riskPictureUpdated(payload) : undefined),
    [payload],
  );
  const firstDayWithItems = model?.days.findIndex((day) => day.items.length > 0) ?? -1;
  const [selection, setSelection] = useState<RiskPictureSelection>();
  const [hovered, setHovered] = useState<RiskPictureSelection>();
  const dayIndex = clampDayIndex(
    selection?.dayIndex ?? (firstDayWithItems === -1 ? 0 : firstDayWithItems),
    model?.days.length ?? 0,
  );

  const handleSelectDay = useCallback((index: number) => {
    setHovered(undefined);
    setSelection(selectRiskPictureDay(index));
  }, []);
  const handleSelectItem = useCallback((index: number, itemId: string) => {
    setHovered(undefined);
    setSelection(selectRiskPictureWarning(index, itemId));
  }, []);
  const handleBack = useCallback(
    () => setSelection((current) => current && clearRiskPictureWarning(current)),
    [],
  );

  // Shared layout flag: collapses the nav, locks body scroll, and resets on navigation.
  const [isFullscreen, layoutStore] = useLayoutStore((s) => s.context.isFullscreen);
  const scrollBeforeFullscreen = useRef(0);
  const handleFullscreenChange = useCallback(
    (value: boolean) => {
      // RootLayout scrolls to the top on every toggle. Restore the pre-fullscreen position on
      // exit, so the page comes back where the user left it.
      if (value) scrollBeforeFullscreen.current = window.scrollY;
      // flushSync also flushes RootLayout's scroll effect, so the restore below wins.
      flushSync(() => layoutStore.trigger.setFullscreen({ value }));
      if (!value) window.scrollTo(0, scrollBeforeFullscreen.current);
    },
    [layoutStore],
  );
  useEffect(() => () => layoutStore.trigger.setFullscreen({ value: false }), [layoutStore]);
  useEffect(() => {
    if (!isFullscreen) return;
    // On window, not document: the cluster chooser and MUI dialogs stop Escape on the way up,
    // so closing those does not also leave fullscreen.
    const exitOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleFullscreenChange(false);
    };
    window.addEventListener("keydown", exitOnEscape);
    return () => window.removeEventListener("keydown", exitOnEscape);
  }, [isFullscreen, handleFullscreenChange]);

  if (!demoPayload && warningSetsQuery.isPending) return null;
  if (!demoPayload && warningSetsQuery.isError) {
    return (
      <div className={classnames(styles.card, styles.loadError)} role="alert">
        {String(t("riskPicture.loadError"))}
      </div>
    );
  }
  if (!model) return null;

  const preview = riskPictureMapPreview(model.days, dayIndex, hovered);
  const mapDayIndex = preview.dayIndex;
  const previewItemId = preview.itemId;

  const header = (
    <header className={styles.header}>
      <h2 className={styles.headerTitle}>{title}</h2>
      <p className={styles.headerDescription}>{description}</p>
      {updated && (
        <p className={styles.headerUpdated}>
          {updated.source
            ? t("widgets.meta.updatedAtBy", {
                date: format(updated.at, "E d MMM yyyy (HH:mm)", {
                  locale: getDateFnsLocale(locale),
                }),
                source: updated.source,
              })
            : t("widgets.meta.updatedAt", {
                date: format(updated.at, "E d MMM yyyy (HH:mm)", {
                  locale: getDateFnsLocale(locale),
                }),
              })}
        </p>
      )}
    </header>
  );

  const commentButton = (
    <Button
      variant="contained"
      className={styles.commentButton}
      onClick={() => setIsCommentOpen(true)}
      sx={{
        backgroundColor: "#01689B",
        borderRadius: "5px",
        boxShadow: "6px 6px 54px 0 rgba(0, 0, 0, 0.05)",
        color: "#fff",
        fontSize: "14px",
        fontWeight: 400,
        gap: "8px",
        height: "40px",
        minHeight: "40px",
        padding: "0 16px",
        textTransform: "none",
        transition: "background-color 150ms ease",
        whiteSpace: "nowrap",
        "&:hover": {
          backgroundColor: "#005a84",
          boxShadow: "6px 6px 54px 0 rgba(0, 0, 0, 0.05)",
        },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        "& .MuiSvgIcon-root": { fontSize: "14px" },
      }}
    >
      <span className={styles.commentButtonLabel}>{t("riskPicture.comment.button")}</span>
      <InfoOutlinedIcon />
    </Button>
  );

  const table = (
    <section
      className={classnames(
        styles.card,
        styles.tableCard,
        isFullscreen && styles.tableCardBehindFullscreen,
      )}
    >
      <div className={styles.tableToolbar}>
        {header}
        {commentButton}
      </div>
      <div className={styles.tableScroll}>
        <RiskPictureTable
          model={model}
          selectedDay={dayIndex}
          onSelectDay={handleSelectDay}
        />
      </div>
    </section>
  );

  const map = (
    <section
      className={classnames(
        styles.card,
        styles.mapCard,
        isFullscreen && styles.mapCardFullscreen,
      )}
    >
      <div className={styles.mapDayToolbar}>
        {model.days[mapDayIndex] && (
          <h3 className={styles.mapDayTitle}>
            {riskPictureDayTitle(model.days[mapDayIndex].date, locale)}
          </h3>
        )}
        <div className={styles.mapDayActions}>
          {commentButton}
          <button
            type="button"
            className={styles.mapFullscreenToggle}
            aria-label={t(
              isFullscreen
                ? "riskPicture.map.exitFullscreen"
                : "riskPicture.map.fullscreen",
            )}
            aria-pressed={isFullscreen}
            onClick={() => handleFullscreenChange(!isFullscreen)}
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>
      <RiskPictureMap
        days={model.days}
        phenomena={model.phenomena}
        dayIndex={dayIndex}
        mapDayIndex={mapDayIndex}
        selectedItemId={selection?.itemId}
        previewItemId={previewItemId}
        onHoverItem={setHovered}
        onSelectDay={handleSelectDay}
        onSelectItem={handleSelectItem}
        onBack={handleBack}
        fullscreen={isFullscreen}
      />
    </section>
  );

  return (
    <div className={styles.widget}>
      {table}
      {map}
      <Dialog
        open={isCommentOpen}
        onClose={() => setIsCommentOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle className={styles.commentDialogTitle}>
          {t("riskPicture.comment.title")}
          <IconButton
            aria-label={t("riskPicture.comment.close")}
            onClick={() => setIsCommentOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {legacyCommentQuery.isPending && <p>{t("riskPicture.comment.loading")}</p>}
          {legacyCommentQuery.isError && (
            <p role="alert">{t("riskPicture.comment.loadError")}</p>
          )}
          {legacyCommentQuery.data && (
            <div className={styles.commentBody}>
              <Html content={legacyCommentQuery.data.description} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
